<template>
  <div class="d-flex flex-column justify-content-end w-100">
    <label
      v-if="itemModel.vars.label"
      :for="labelFor"
      :class="
        'form-label' +
        (itemModel.vars.label_attr && itemModel.vars.label_attr.class ? ' ' + itemModel.vars.label_attr.class : '') +
        (itemModel.vars.required ? ' required' : '')
      "
    >
      {{ index ? index + ". " : "" }}{{ itemModel.vars.label }}{{ itemModel.vars.required ? "*" : "" }}
      <span
        v-if="itemModel.vars.help && !isMobile()"
        v-tooltip="{ title: itemModel.vars.help, textWrap: true }"
        class="form-info ms-1"
      >
        <FontAwesomeIcon icon="fa-light fa-circle-question" />
      </span>
    </label>
    <div v-if="isMobile() && itemModel.vars.help" class="form-info">
      <FontAwesomeIcon icon="fa-light fa-circle-question" />
      {{ itemModel.vars.help }}
    </div>
    <div v-if="isPasswordType()" class="position-relative d-flex">
      <PasswordTypeComponent
        :inputId="itemModel.vars.id"
        :password="itemModel.vars.value"
        :model="itemModel"
        :itemClass="computedClass"
        :displayInfo="itemModel.vars.attr['displayInfo']"
        @changePassword="blur"
        @checkInput="change"
      />
    </div>
    <div
      v-else
      :class="{
        'position-relative': itemModel.vars.block_prefixes[1] === 'number',
        'w-100': true,
        'd-flex align-items-center ':
          itemModel?.vars?.attr?.['data-button'] || itemModel?.vars?.attr?.['data-button-attr']?.dataUrl,
      }"
    >
      <input
        v-if="!itemModel.vars.hide"
        :id="itemModel.vars.id"
        v-model="itemModel.vars.value"
        :readonly="itemModel.vars.attr && itemModel.vars.attr.readonly"
        :class="
          computedClass +
          (itemModel.vars.attr && itemModel.vars.attr.class ? ' ' + itemModel.vars.attr.class : '') +
          (itemModel.vars?.attr?.currency ? ' currency' : '')
        "
        :disabled="itemModel.vars.disabled === true"
        :name="language ? itemModel.vars.name + '-' + language : itemModel.vars.name"
        :placeholder="itemModel?.vars?.attr?.placeholder ?? ''"
        :required="itemModel.vars.required === true"
        :type="itemModel.vars.block_prefixes[1]"
        :maxlength="itemModel?.vars?.attr?.maxlength ?? ''"
        :aria-invalid="itemModel.vars.error ? 'true' : 'false'"
        :aria-describedby="ariaDescribedBy"
        @blur="blur"
        @change="change"
        @focus="focus"
        @input="input"
      />
      <div class="input__operator">
        <div v-if="displayOperatorButton()" class="input__nav">
          <button type="button" @click="stepUp">
            <FontAwesomeIcon icon="fa-light fa-plus" />
          </button>
          <button type="button" @click="stepDown">
            <FontAwesomeIcon icon="fa-light fa-minus" />
          </button>
        </div>
        <div v-if="itemModel.vars?.attr?.currency" class="input__currency">
          {{ itemModel.vars?.attr?.currency }}
        </div>
      </div>
      <ButtonComponent
        v-if="itemModel?.vars?.attr?.['data-button-attr']?.dataUrl"
        className="success ms-2"
        :icon="itemModel.vars.attr['data-button-attr'].icon"
        :tooltip="itemModel.vars.attr['data-button-attr'].buttonTooltip"
        @buttonClick="buttonClick(itemModel.vars.attr['data-button-attr'].dataUrl)"
      />
      <button
        v-if="itemModel?.vars?.attr?.['data-button']"
        v-tooltip="{ title: $t('regenerate') }"
        type="button"
        class="ms-2 btn btn-success"
        @click="buttonAction()"
      >
        <FontAwesomeIcon
          v-if="itemModel.vars.attr['data-button-icon']"
          :icon="'fa-light fa-' + itemModel.vars.attr['data-button-icon']"
        />
      </button>
    </div>
    <div
      v-if="model.vars.error || errors"
      :id="itemModel.vars.id + '-error'"
      class="invalid-feedback d-block"
      role="alert"
    >
      {{ model.vars.error }}
      {{ errors }}
    </div>
  </div>
</template>

<script>
import { formValidator } from "@/services/form/form-validator";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import PasswordTypeComponent from "@/components/form/PasswordTypeComponent.vue";
import { stringManager } from "@/services/utilities/string-manager";
import ButtonComponent from "@/components/utils/buttons/ButtonComponent.vue";

// Un brouillon est une requete d'enregistrement complete. Emis a chaque frappe, plusieurs
// requetes partent en concurrence et le serveur, qui cherche la reponse existante puis la cree
// si elle manque, en insere une par requete. Les doublons faussent ensuite silencieusement la
// note du questionnaire (ST143-TI-18146). Le delai couvre une rafale de frappe sans etre
// perceptible a la saisie.
const DRAFT_DEBOUNCE_MS = 500;

export default {
  name: "InputTypeComponent",
  components: {
    ButtonComponent,
    PasswordTypeComponent,
    FontAwesomeIcon,
  },
  props: {
    model: {
      type: Object,
      required: true,
    },
    language: {
      type: String,
      required: false,
      default: "fr",
    },
    index: {
      type: Number,
      required: false,
      default: null,
    },
    forcedDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    describedBy: {
      type: String,
      required: false,
      default: null,
    },
  },
  emits: ["buttonClick", "inputModel", "input", "draft", "focus", "update", "focused", "save", "buttonAction"],
  data() {
    return {
      itemModel: this.model,
      errors: null,
      draftTimer: null,
    };
  },
  computed: {
    ariaDescribedBy() {
      const ids = [this.describedBy, this.itemModel.vars.error ? this.itemModel.vars.id + "-error" : null].filter(
        Boolean,
      );
      return ids.length ? ids.join(" ") : null;
    },

    labelFor() {
      return this.itemModel.vars.hide && !this.isPasswordType() ? undefined : this.itemModel.vars.id;
    },
    computedClass() {
      if (this.itemModel.vars.error || this.itemModel.vars.isInvalid) {
        return "form-control is-invalid";
      } else if (
        !this.itemModel.vars.error &&
        this.itemModel.vars.required &&
        this.itemModel.vars.value !== null &&
        this.itemModel.vars.value !== undefined &&
        this.itemModel.vars.value !== ""
      ) {
        return "form-control is-valid";
      }
      return "form-control";
    },
  },
  watch: {
    model() {
      this.itemModel.vars.value = this.model?.vars?.value;
      this.normalizeNumberValue();
    },
  },
  mounted() {
    this.normalizeNumberValue();
  },
  beforeUnmount() {
    this.flushDraft();
  },
  methods: {
    normalizeNumberValue() {
      if (
        this.itemModel.vars.block_prefixes[1] === "number" &&
        this.itemModel.vars.value &&
        this.itemModel.vars.value.length
      ) {
        this.itemModel.vars.value = parseFloat(this.itemModel?.vars?.value?.replace(",", "."));
      }
    },
    blur() {
      this.flushDraft();
      this.valid(true);
      this.$emit("update", this.itemModel.vars.value, this.index);
    },
    input() {
      this.formatNafCode();
      this.valid();
      this.$emit("update", this.itemModel.vars.value, this.index);
      this.$emit("input", this.itemModel.vars.value, this.index);
      this.$emit("inputModel", this.itemModel);
      this.scheduleDraft();
    },

    scheduleDraft() {
      clearTimeout(this.draftTimer);
      this.draftTimer = setTimeout(() => this.$emit("draft", this.itemModel), DRAFT_DEBOUNCE_MS);
    },

    flushDraft() {
      if (this.draftTimer) {
        clearTimeout(this.draftTimer);
        this.draftTimer = null;
        this.$emit("draft", this.itemModel);
      }
    },
    change() {
      this.valid();
      this.$emit("update", this.itemModel.vars.value, this.index);
    },
    focus() {
      this.$emit("focus");
    },
    isMobile() {
      return window.innerWidth < 992;
    },
    valid(blur = false) {
      this.enforceDigit(this.itemModel.vars);
      if (blur) {
        const value = this.itemModel?.vars?.value;
        if (value != null) {
          this.itemModel.vars.value = String(value).trim();
        }
        this.$emit("save");
      }
      formValidator.validModel(this.itemModel);
      formValidator.handleNumberError(this.itemModel);
    },

    stepUp() {
      this.itemModel.vars.value++;
      this.valid();
      this.$emit("update", this.itemModel.vars.value, this.index);
      this.$emit("input", this.itemModel.vars.value, this.index);
      this.scheduleDraft();
      this.$emit("save");
      this.change();
    },

    stepDown() {
      this.itemModel.vars.value--;
      this.valid();
      this.$emit("update", this.itemModel.vars.value, this.index);
      this.$emit("input", this.itemModel.vars.value, this.index);
      this.scheduleDraft();
      this.$emit("save");
      this.change();
    },

    isPasswordType() {
      return this.itemModel.vars.block_prefixes[1] === "text" && this.itemModel.vars.block_prefixes[2] === "password";
    },
    formatNafCode() {
      if (this.itemModel.vars.name === "nafCode") {
        let val = this.itemModel.vars.value;

        if (val.length > 2 && val[2] !== ".") {
          this.itemModel.vars.value = val.slice(0, 2) + "." + val.slice(2);
        }
      }
    },

    enforceDigit(vars) {
      if (
        vars &&
        vars.value &&
        vars.value !== "" &&
        vars.block_prefixes[1] === "number" &&
        vars.attr &&
        vars.attr["data-max-digit"] !== undefined &&
        vars.attr["data-max-digit"] !== null
      ) {
        vars.value = stringManager.enforceDigit(vars.value, vars.attr["data-max-digit"]);
      }
    },

    displayOperatorButton() {
      return (
        this.itemModel?.vars?.block_prefixes[1] === "number" &&
        !this.itemModel?.vars?.hide &&
        !this.itemModel?.vars?.disabled &&
        !this.itemModel?.vars?.attr?.["hideOperatorButtons"]
      );
    },

    buttonAction() {
      this.$emit("buttonAction", this.itemModel?.vars?.attr["data-button"]);
    },

    buttonClick(url) {
      this.$emit("buttonClick", url);
    },
  },
};
</script>

<style lang="scss" scoped>
.non-empty {
  overflow: auto;
}

input[type="number"] {
  -webkit-appearance: textfield;
  -moz-appearance: textfield;
  appearance: textfield;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
}

.input__operator {
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
}

.input__nav {
  display: flex;
  padding: 10px 0;

  button {
    border: none;
    background: none;
    color: #3b3f5c;
    display: flex;
    align-items: center;
    padding: 5px 10px;

    @include dark-theme {
      color: $white;
    }

    &:hover {
      color: var(--primary-color);
    }

    &:first-child {
      border-right: 1px solid $grey;
    }
  }
}

.input__currency {
  width: 40px;
  height: 100%;
  background: $m_color_3;
  border-left: none;
  border-radius: 0 $radius $radius 0;
  position: relative;
  right: -1px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;

  @include dark-theme {
    background: $dark-theme-bg;
    border-left: 1px solid $m-color_12;
    color: $white;
  }
}

input[type="number"] {
  &.form-control.is-valid,
  &.form-control.is-invalid {
    background: $white;
  }
}
</style>
